/**
 * Screen 15 — Sign in (returning user).
 *
 * Google-only. Returning users get a recognition chip (last-signed-in
 * display name + workspace). Editorial aside on the right is the only
 * place outside the briefing surfaces where Cormorant Garamond is
 * allowed in the Pro app — by designer rule.
 *
 * Auth handler: if the user is `active`, route to their workspace home.
 * If `pending`, route to the pending screen. New user (no profile yet)
 * routes to SignUp so they pick a role.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithPopup } from "firebase/auth";
import { track, trackOnce } from "@/lib/track";
import { doc, getDoc } from "firebase/firestore";
import { AppNav } from "@/components/AppNav";
import { IconGoogle } from "@/components/icons";
import { auth, db, googleProvider } from "@/firebase";
import { Routes, homeFor } from "@/lib/routes";

export function SignIn() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackOnce("signin_view");
  }, []);

  const handleSignIn = async () => {
    if (busy) return;
    // Fired BEFORE the popup: the gap between this and signup_complete is the
    // count of people who reached for the door and did not get through.
    track("signup_click", { variant: "signin" });
    setBusy(true);
    setError(null);
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      const snap = await getDoc(doc(db, "pro_users", cred.user.uid));
      if (!snap.exists()) {
        // Brand-new user; force them through role selection.
        nav(Routes.SignUp, { replace: true });
        return;
      }
      const data = snap.data();
      if (data.status === "active") {
        // homeFor is the single source of truth. The inline ternary sent every
        // MFD to the RETAIL workspace, whose empty personal portfolio then
        // bounced them to the day-zero upload screen.
        nav(homeFor(data.role), { replace: true });
      } else if ((data.role === "ifa" || data.role === "mfd" || data.role === "ra") && !data.sebiRegistration) {
        // SignUp routes BOTH adviser personas here; this checked "ifa" only,
        // so an MFD who abandoned the ARN step was sent to "Account in
        // review" — reviewing a submission they were never asked to make.
        // ApprovalPending has no link back, so they waited forever.
        nav(Routes.IFAVerify, { replace: true });
      } else {
        nav(Routes.Pending, { replace: true });
      }
    } catch (e: any) {
      console.error("[signin] failed:", e);
      setError(
        e?.code === "auth/popup-closed-by-user"
          ? "Sign-in popup was closed before you finished."
          : "Sign-in failed. Try again, or refresh the page.",
      );
      setBusy(false);
    }
  };

  return (
    <div className="col" style={{ minHeight: "100%", background: "var(--paper-2)" }}>
      <AppNav mode="signin" />

      <main
        style={{
          flex: 1,
          display: "flex",
          // MUST wrap. Without it this row is card(440) + gap(64) + aside(360) +
          // padding(96) = 960px that cannot shrink, and on a phone iOS Safari
          // widens its LAYOUT viewport to fit — which silently switches the whole
          // app to desktop rules, because every `max-width: 600px` block (including
          // the main{overflow-x:clip} guard meant to catch exactly this) stops
          // matching. The blown viewport then persists across client-side routes
          // until a reload, so one un-wrapped row here broke the landing page too.
          // Note a JS breakpoint hook cannot fix this: matchMedia reads the same
          // blown viewport. It has to be prevented in CSS, not detected.
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          // clamp, not a media query: these scale with the viewport whatever it
          // reports, so the gutter can never be 48px on a 414px screen.
          padding: "40px clamp(16px, 5vw, 48px) 56px",
          gap: "clamp(24px, 6vw, 64px)",
        }}
      >
        <div className="auth-card">
          <div className="crest">S</div>
          <div>
            <div className="kicker">
              <span className="sq" />
              Welcome back
            </div>
            <h1
              style={{
                fontFamily: "var(--sans)",
                fontSize: 28,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
                marginTop: 8,
              }}
            >
              Sign in to StockSense&nbsp;AI Pro.
            </h1>
            <p
              className="muted"
              style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.6 }}
            >
              One account, one role. We'll take you back to the workspace you
              used last time.
            </p>
          </div>

          <button
            className="btn btn-google btn-lg btn-block"
            style={{ justifyContent: "center" }}
            onClick={handleSignIn}
            disabled={busy}
          >
            <IconGoogle />
            <span>{busy ? "Opening Google sign-in…" : "Continue with Google"}</span>
          </button>

          {error && (
            <p
              className="text-loss"
              style={{ fontSize: 12.5, lineHeight: 1.5 }}
            >
              {error}
            </p>
          )}

          <div
            style={{
              borderTop: "1px solid var(--line-soft)",
              paddingTop: 14,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <p
              style={{
                fontSize: 11.5,
                color: "var(--ink-light)",
                lineHeight: 1.55,
              }}
            >
              Trouble signing in? Make sure you use the same Google account you
              signed up with. If your role has changed, contact support — we
              can move you to the other workspace without losing your
              briefings.
            </p>
            <a
              href="https://wa.me/917757025257"
              target="_blank" rel="noopener noreferrer"
              style={{
                fontSize: 12,
                color: "var(--brand)",
                textDecoration: "none",
                fontFamily: "var(--mono)",
                letterSpacing: ".06em",
              }}
            >
              WhatsApp support →
            </a>
          </div>
        </div>

        <div className="auth-aside">
          <div className="kicker">
            <span className="sq" />
            Research pipeline
          </div>
          <p className="quote">
            A research analyst's job is to be wrong out loud — quickly,
            cheaply, and in a way you can re-examine next quarter.
          </p>
          <div className="author">— StockSense AI Pipeline · v3.4</div>

          {/* REMOVED 2026-09-05: "Briefings · last 30 days 4,217", "Median
              runtime 02:47" and "NSE coverage 1,962 tickers" were hardcoded
              literals presented as live statistics on an auth screen. Against
              ~11 lifetime retail accounts and ~Rs 97/report COGS, 4,217
              briefings in 30 days implies ~Rs 4.1 lakh of monthly model spend —
              it was not merely stale, it was never true. The same rule was
              already applied once when the fabricated NIFTY block was deleted
              from AppNav rather than re-invented. If we want numbers here they
              must come from a verified aggregate with a stated window. */}
        </div>
      </main>

      <div
        style={{
          padding: "14px 32px",
          borderTop: "1px solid var(--line)",
          textAlign: "center",
          fontSize: 11,
          color: "var(--ink-light)",
          fontFamily: "var(--mono)",
          letterSpacing: ".10em",
        }}
      >
        Educational technology tool — not a SEBI-registered RA or IA.
      </div>
      <div style={{ textAlign: "center", marginTop: 8, fontSize: 11, opacity: 0.7 }}>
        <a href="/terms.html" style={{ color: "inherit" }}>Terms</a> ·{" "}
        <a href="/privacy.html" style={{ color: "inherit" }}>Privacy</a> ·{" "}
        <a href="/refund.html" style={{ color: "inherit" }}>Refund &amp; Cancellation</a>
      </div>
    </div>
  );
}
